import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { buildCrewLookupMaps, resolveRouteForDrive } from './routeDriveResolver'

const NULL_RESULT = { driveHours: 0, driveMiles: 0, source: null }

// Module-level cache of self-fetched routes, shared across hook instances so
// PropertyOverview (no `routes` prop) only hits /territory/properties once.
let _routesCache = null
export const __resetRoutesCache = () => { _routesCache = null }

// Looks up a property's realistic inbound inter-stop drive from its live (or
// scenario) route. Returns { driveHours, driveMiles, source } or {source:null}
// on any miss (unscheduled / unresolved / null leg / request error) so the
// caller falls back to the yard estimate. `source` reflects the route response
// meta so the UI labels honestly (never claims "scenario" on a silent fallback).
export const useRouteInterStopDrive = ({ property, routes, selectedScenarioId, url }) => {
  // Initial source is `undefined` (not null) so callers waiting on the first
  // resolution can distinguish "not resolved yet" from a settled null miss.
  const [result, setResult] = useState({ driveHours: 0, driveMiles: 0, source: undefined })
  const [resolvedRoutes, setResolvedRoutes] = useState(routes?.length ? routes : (_routesCache || null))

  // When no routes are passed (e.g. PropertyOverview), self-fetch them from the
  // territory endpoint once and cache module-wide for other instances.
  useEffect(() => {
    let cancelled = false
    if (routes?.length || _routesCache || !url) return
    axios.get(`${url}/api/hmi/territory/properties`)
      .then(res => {
        const fetched = Array.isArray(res?.data?.routes) ? res.data.routes : []
        if (fetched.length) _routesCache = fetched // only cache a real result; don't poison on empty/transient failure
        if (!cancelled) setResolvedRoutes(fetched)
      })
      .catch(() => { if (!cancelled) setResolvedRoutes([]) })
    return () => { cancelled = true }
  }, [routes, url])

  const effectiveRoutes = routes?.length ? routes : (resolvedRoutes || [])
  const maps = useMemo(() => buildCrewLookupMaps(effectiveRoutes), [effectiveRoutes])
  const pid = property?.propertyId ?? property?.PropertyID
  const resolved = useMemo(() => (property ? resolveRouteForDrive(property, maps) : null), [property, maps])
  const crewId = resolved?.crewId || null
  const date = resolved?.date || null

  // True while we're self-fetching routes and haven't gotten them back yet; in
  // that window crewId can't resolve, but settling to a null miss would race the
  // pending fetch — so we hold the result unsettled (source undefined) instead.
  const routesPending = !routes?.length && resolvedRoutes === null

  useEffect(() => {
    let cancelled = false
    if (routesPending) return
    if (!crewId || !date) { setResult(NULL_RESULT); return }
    const base = selectedScenarioId
      ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${crewId}/route`
      : `${url}/api/hmi/territory/crews/${crewId}/route`
    axios.get(base, { params: { date } })
      .then(res => {
        if (cancelled) return
        const stops = res?.data?.stops || []
        const stop = stops.find(s => Number(s.aspirePropertyId ?? s.propertyId) === Number(pid))
        const mins = stop ? stop.driveTimeMinutes : null
        if (stop == null || mins == null) { setResult(NULL_RESULT); return }
        setResult({
          driveHours: Number(mins) / 60,
          driveMiles: Number(stop.driveMiles) || 0,
          source: res?.data?.meta?.source || res?.data?.meta?.mode || 'route'
        })
      })
      .catch(() => { if (!cancelled) setResult(NULL_RESULT) })
    return () => { cancelled = true }
  }, [crewId, date, selectedScenarioId, url, pid, routesPending])

  return result
}
